import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    FunnelSimple, Receipt, ArrowsClockwise, CheckCircle,
    ClockClockwise, Hourglass, DownloadSimple, ShieldCheck,
    Warning, CalendarBlank, ListChecks, Coins, Stack,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { sortByDate, SortableHeader } from "../../lib/dateSort";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import DateFilterButton from "../../components/DateFilterButton";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

// 5-stage lifecycle: Deducted → Received → Verified → Claimed → Adjusted
const STAGES = [
    { key: "Deducted", tone: "rose", icon: <Hourglass size={11} weight="fill" />, hint: "Form 16A pending" },
    { key: "Received", tone: "amber", icon: <ClockClockwise size={11} weight="fill" />, hint: "Form 16A in hand" },
    { key: "Verified", tone: "indigo", icon: <CheckCircle size={11} weight="fill" />, hint: "Reconciled with 26AS" },
    { key: "Claimed", tone: "violet", icon: <ListChecks size={11} weight="fill" />, hint: "Offset against IT liability" },
    { key: "Adjusted", tone: "emerald", icon: <CheckCircle size={11} weight="fill" />, hint: "Year-end closed" },
];

const TONE_BG = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const EMPTY_FILTERS = { client_id: "", date_from: "", date_to: "", tds_status: "", fy: "" };

export default function TdsReport() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    // Amendment 5 (UAT-002, 2026-02-12) — client-side sort on Invoice Date
    // and Payment Date columns. `null` => preserve backend default
    // (payment_date DESC). Operator can toggle via the sortable column
    // headers in the detail table.
    const [sortState, setSortState] = useState(null);

    const load = async () => {
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
            const [r, c] = await Promise.all([
                api.get("/billing/reports/compliance/tds", { params }),
                clients.length ? Promise.resolve({ data: { items: clients } }) : api.get("/billing/client/list"),
            ]);
            setData(r.data);
            if (c.data?.items && !clients.length) setClients(c.data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load TDS report"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    // Amendment 5 — sorted view of the detail items (no-op when sortState is null).
    const sortedItems = useMemo(
        () => sortByDate(data?.items || [], sortState),
        [data, sortState],
    );

    const updateStage = async (paymentId, stage) => {
        setSavingId(paymentId);
        try {
            await api.patch(`/billing/payment/${paymentId}/tds-status`, { tds_status: stage });
            toast.success(`Marked ${stage}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        } finally {
            setSavingId(null);
        }
    };

    const csv = () => {
        if (!data?.items?.length) return;
        const cols = ["FY", "Client", "PAN", "Invoice No", "Invoice Date", "Invoice Amount", "TDS %", "TDS Amount", "Payment", "Payment Date", "Mode", "Reference", "Stage", "Days in stage"];
        const rows = data.items.map((r) => [
            r.fy, r.client_name, r.client_pan, r.invoice_number, r.invoice_date, r.invoice_amount,
            r.tds_percentage, r.tds_amount, r.payment_received, r.payment_date, r.payment_mode,
            r.reference_number, r.tds_status, r.days_in_stage,
        ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tds-report-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const reset = () => setFilters(EMPTY_FILTERS);

    if (loading && !data) return <div data-testid="tds-page-loading"><PageHeader eyebrow="Compliance · TDS" title="Loading…" /></div>;
    if (!data) return null;

    const lc = data.lifecycle || { amounts: {}, counts: {} };
    const att = data.attention || { missing_certificates: [], unverified: [], missing_count: 0, unverified_count: 0 };
    const fyOptions = (data.by_fy || []).map((f) => ({ value: f.fy, label: f.fy }));

    return (
        <div data-testid="tds-report-page">
            <PageHeader
                eyebrow="Compliance · TDS Credit Tracking"
                title={
                    <span className="flex items-center gap-3">
                        <ShieldCheck size={22} weight="duotone" className="text-amber-600" />
                        TDS Lifecycle
                    </span>
                }
                description="TDS is a tax credit, not money owed. Track each deduction from Deducted → Received → Verified → Claimed → Adjusted."
                actions={
                    <Button variant="outline" size="sm" onClick={csv} disabled={!data.items.length} className="gap-1.5" data-testid="tds-export-csv-btn">
                        <DownloadSimple size={13} /> Export CSV
                    </Button>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Lifecycle KPI strip — 5 stages */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="tds-lifecycle-strip">
                    {STAGES.map((s) => (
                        <Kpi
                            key={s.key}
                            label={s.key}
                            value={formatINR(lc.amounts[s.key] || 0)}
                            sub={`${lc.counts[s.key] || 0} payment${(lc.counts[s.key] || 0) === 1 ? "" : "s"} · ${s.hint}`}
                            icon={s.icon}
                            tone={s.tone}
                            testid={`tds-stage-${s.key.toLowerCase()}`}
                        />
                    ))}
                </div>

                {/* Headline + Attention */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="reedos-card p-4 lg:col-span-1" data-testid="tds-headline">
                        <div className="reedos-eyebrow flex items-center gap-1.5"><Coins size={11} /> Total TDS receivable</div>
                        <div className="font-display font-bold reedos-data text-3xl mt-1 text-amber-700">{formatINR(data.totals.tds_amount)}</div>
                        <div className="text-[11px] text-slate-500 mt-1">{data.total} deductions across {data.by_fy?.length || 0} FY{(data.by_fy?.length || 0) === 1 ? "" : "s"}</div>
                        <div className="border-t border-slate-100 mt-3 pt-3 grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[10px] text-slate-500 uppercase font-semibold">Cash collected alongside</div>
                                <div className="font-mono font-semibold text-emerald-700">{formatINR(data.totals.payment_received)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] text-slate-500 uppercase font-semibold">Invoice value</div>
                                <div className="font-mono font-semibold text-slate-700">{formatINR(data.totals.invoice_amount)}</div>
                            </div>
                        </div>
                    </div>
                    <AttentionCard
                        title="Missing certificates"
                        subtitle="TDS deducted &gt; 30 days ago, Form 16A still pending"
                        count={att.missing_count}
                        rows={att.missing_certificates}
                        tone="rose"
                        testid="tds-attention-missing"
                    />
                    <AttentionCard
                        title="Unverified certificates"
                        subtitle="Certificate received &gt; 30 days, not yet verified against 26AS"
                        count={att.unverified_count}
                        rows={att.unverified}
                        tone="amber"
                        testid="tds-attention-unverified"
                    />
                </div>

                {/* Iter 53 — Month-wise summary (by settlement/payment month).
                    TDS belongs to the month it was deducted/paid, not the
                    invoice month — matches Indian tax authority reconciliation
                    (Form 16A quarter / 26AS / TDS deposit cycles). */}
                {data.by_month && data.by_month.length > 0 && (
                    <div className="reedos-card overflow-hidden" data-testid="tds-month-summary">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                            <CalendarBlank size={14} className="text-slate-500" />
                            <div className="font-display font-bold text-sm">Month-wise summary</div>
                            <div className="text-[11px] text-slate-500 ml-2">
                                By settlement / payment month · matches TDS deposit + Form 16A cycle
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Month</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">FY</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Settlements</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Total TDS</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Cash alongside</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Invoice value</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Pending</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Claimed / adj</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.by_month.map((m) => (
                                        <tr key={m.month} className="border-t border-slate-100 reedos-row-hover" data-testid={`tds-month-row-${m.month}`}>
                                            <td className="px-4 py-2.5 text-xs font-mono font-semibold text-slate-900">{m.month}</td>
                                            <td className="px-4 py-2.5 text-xs font-mono text-slate-500">FY {m.fy}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600">{m.settlement_count}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-slate-900">{formatINR(m.total_tds)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-emerald-700">{formatINR(m.cash_collected)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600">{formatINR(m.invoice_value)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-amber-700">{formatINR(m.pending)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-violet-700">{formatINR(m.claimed)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* FY-wise summary */}
                {data.by_fy && data.by_fy.length > 0 && (
                    <div className="reedos-card overflow-hidden" data-testid="tds-fy-summary">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                            <CalendarBlank size={14} className="text-slate-500" />
                            <div className="font-display font-bold text-sm">FY-wise summary</div>
                            <div className="text-[11px] text-slate-500 ml-2">Total · Pending · Claimed · Adjusted</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">FY</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Rows</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Total TDS</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Pending</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Claimed</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Adjusted</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.by_fy.map((f) => {
                                        const closedPct = f.total_tds > 0
                                            ? Math.round(((f.claimed + f.adjusted) / f.total_tds) * 100)
                                            : 0;
                                        return (
                                            <tr key={f.fy} className="border-t border-slate-100 reedos-row-hover" data-testid={`tds-fy-row-${f.fy}`}>
                                                <td className="px-4 py-3 text-xs font-mono font-semibold text-slate-900">FY {f.fy}</td>
                                                <td className="px-4 py-3 text-xs text-right reedos-data text-slate-600">{f.rows}</td>
                                                <td className="px-4 py-3 text-xs text-right reedos-data font-bold text-slate-900">{formatINR(f.total_tds)}</td>
                                                <td className="px-4 py-3 text-xs text-right reedos-data text-amber-700">{formatINR(f.pending)}</td>
                                                <td className="px-4 py-3 text-xs text-right reedos-data text-violet-700">{formatINR(f.claimed)}</td>
                                                <td className="px-4 py-3 text-xs text-right reedos-data text-emerald-700">{formatINR(f.adjusted)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${closedPct}%` }} />
                                                        </div>
                                                        <span className="text-[10px] font-mono text-slate-500 w-10 text-right">{closedPct}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="reedos-card p-4" data-testid="tds-filters">
                    <div className="flex items-center justify-between mb-3">
                        <div className="reedos-eyebrow flex items-center gap-1.5"><FunnelSimple size={11} /> Filters</div>
                        <Button variant="ghost" size="sm" onClick={reset} className="text-[11px] gap-1 text-slate-500 hover:text-slate-900" data-testid="tds-filters-reset">
                            <ArrowsClockwise size={11} /> Reset
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <DateFilterButton label="From · payment date" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} testid="tds-filter-date-from" />
                        <DateFilterButton label="To · payment date" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} testid="tds-filter-date-to" />
                        <FilterSelect label="Client" value={filters.client_id} onChange={(v) => setFilters({ ...filters, client_id: v })} options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} testid="tds-filter-client" />
                        <FilterSelect
                            label="Stage"
                            value={filters.tds_status}
                            onChange={(v) => setFilters({ ...filters, tds_status: v })}
                            options={[{ value: "", label: "All stages" }, ...STAGES.map((s) => ({ value: s.key, label: s.key }))]}
                            testid="tds-filter-stage"
                        />
                        <FilterSelect
                            label="FY"
                            value={filters.fy}
                            onChange={(v) => setFilters({ ...filters, fy: v })}
                            options={[{ value: "", label: "All FYs" }, ...fyOptions]}
                            testid="tds-filter-fy"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="reedos-card overflow-hidden" data-testid="tds-table">
                    {data.items.length === 0 ? (
                        <EmptyState icon={<ShieldCheck size={36} weight="thin" />} title="No TDS rows" subtitle="Capture TDS while recording a payment to see it here." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">FY</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Client</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Invoice</th>
                                        <SortableHeader
                                            field="invoice_date"
                                            label="Invoice Date"
                                            sortState={sortState}
                                            onSort={setSortState}
                                            testid="tds-sort-invoice-date"
                                        />
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">TDS %</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">TDS</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Payment</th>
                                        <SortableHeader
                                            field="payment_date"
                                            label="Pay Date"
                                            sortState={sortState}
                                            onSort={setSortState}
                                            testid="tds-sort-payment-date"
                                        />
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Stage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.map((r) => {
                                        const stageDef = STAGES.find((s) => s.key === r.tds_status) || STAGES[0];
                                        return (
                                            <tr key={r.payment_id} className="border-t border-slate-100 reedos-row-hover" data-testid={`tds-row-${r.payment_id}`}>
                                                <td className="px-4 py-2 text-[11px] font-mono text-slate-600">{r.fy}</td>
                                                <td className="px-4 py-2 text-xs">
                                                    <Link to={`/clients/${r.client_id}/ledger`} className="font-medium text-slate-900 hover:underline">{r.client_name}</Link>
                                                    {r.client_pan && <div className="text-[10px] text-slate-500 font-mono">{r.client_pan}</div>}
                                                </td>
                                                <td className="px-4 py-2 text-xs">
                                                    <Link to={`/invoices/${r.invoice_id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.invoice_number}</Link>
                                                    <div className="text-[10px] text-slate-500">{formatDate(r.invoice_date)}</div>
                                                </td>
                                                <td className="px-4 py-2 text-xs text-slate-700 reedos-data" data-testid={`tds-invoice-date-${r.payment_id}`}>{formatDate(r.invoice_date)}</td>
                                                <td className="px-4 py-2 text-xs text-right reedos-data text-slate-600">{r.tds_percentage > 0 ? `${r.tds_percentage}%` : "—"}</td>
                                                <td className="px-4 py-2 text-xs text-right reedos-data font-bold text-amber-700">{formatINR(r.tds_amount)}</td>
                                                <td className="px-4 py-2 text-xs text-right reedos-data text-emerald-700">{formatINR(r.payment_received)}</td>
                                                <td className="px-4 py-2 text-xs text-slate-700 reedos-data" data-testid={`tds-payment-date-${r.payment_id}`}>{formatDate(r.payment_date)}</td>
                                                <td className="px-4 py-2 text-xs">
                                                    <RoleGuard
                                                        permission="payment:write"
                                                        fallback={
                                                            <span className={classJoin("inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border", TONE_BG[stageDef.tone])}>
                                                                {stageDef.icon} {r.tds_status}
                                                            </span>
                                                        }
                                                    >
                                                        <select
                                                            value={r.tds_status}
                                                            onChange={(e) => updateStage(r.payment_id, e.target.value)}
                                                            disabled={savingId === r.payment_id}
                                                            className={classJoin("h-7 px-2 text-[11px] font-semibold rounded border", TONE_BG[stageDef.tone])}
                                                            data-testid={`tds-stage-select-${r.payment_id}`}
                                                            title={`${r.days_in_stage} day${r.days_in_stage === 1 ? "" : "s"} in this stage`}
                                                        >
                                                            {STAGES.map((s) => (
                                                                <option key={s.key} value={s.key}>{s.key}</option>
                                                            ))}
                                                        </select>
                                                    </RoleGuard>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t border-slate-200">
                                    <tr className="font-semibold">
                                        <td colSpan={4} className="px-4 py-3 text-right reedos-eyebrow">Total</td>
                                        <td className="px-4 py-3 text-right reedos-data text-amber-700 font-bold">{formatINR(data.totals.tds_amount)}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-emerald-700">{formatINR(data.totals.payment_received)}</td>
                                        <td colSpan={2}></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        indigo: "text-indigo-700",
        violet: "text-violet-700",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div>}
        </div>
    );
}

function AttentionCard({ title, subtitle, count, rows, tone, testid }) {
    const tones = {
        rose: "bg-rose-50/40 border-rose-200 text-rose-900",
        amber: "bg-amber-50/40 border-amber-200 text-amber-900",
    };
    return (
        <div className={classJoin("rounded-md border p-4 lg:col-span-1", tones[tone])} data-testid={testid}>
            <div className="flex items-center gap-1.5 reedos-eyebrow">
                <Warning size={11} weight="fill" /> {title}
            </div>
            <div className="font-display font-bold reedos-data text-2xl mt-1 leading-none">{count}</div>
            <div className="text-[11px] mt-1 leading-tight opacity-80" dangerouslySetInnerHTML={{ __html: subtitle }} />
            {count > 0 && (
                <ul className="mt-3 space-y-1.5 text-[11px]">
                    {rows.slice(0, 4).map((r) => (
                        <li key={r.payment_id} className="flex items-center justify-between gap-2 border-t border-current/10 pt-1.5" data-testid={`${testid}-row-${r.payment_id}`}>
                            <Link to={`/invoices/${r.invoice_id}`} className="font-mono font-semibold hover:underline truncate">
                                {r.invoice_number}
                            </Link>
                            <span className="truncate flex-1 text-current/80">{r.client_name}</span>
                            <span className="font-mono font-bold whitespace-nowrap">{formatINR(r.tds_amount)}</span>
                            <span className="font-mono opacity-70 whitespace-nowrap">{r.days_pending}d</span>
                        </li>
                    ))}
                    {rows.length > 4 && (
                        <li className="text-[10px] opacity-60 pt-1">+{rows.length - 4} more</li>
                    )}
                </ul>
            )}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid={testid}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
